import React from 'react';
import { RecipientBadge } from './RecipientBadge';
import { RecipientSummary } from '@/types/pre-approval';
import { formatPhoneNumber } from '@/utils/formatCurrency';

const destinationFor = (r?: { email?: string; phone?: string | null }): string | undefined => {
  if (!r) {
    return undefined;
  }
  if (r.email) {
    return r.email;
  }
  if (r.phone) {
    return formatPhoneNumber(r.phone);
  }
  return undefined;
};

interface RecipientBadgeGroupProps {
  recipientsSummary?: RecipientSummary;
  onBadgeClick?: () => void;
}

export const RecipientBadgeGroup: React.FC<RecipientBadgeGroupProps> = ({
  recipientsSummary,
  onBadgeClick
}) => {
  // If no recipient summary, show all as 'none' (backward compatibility)
  if (!recipientsSummary) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex flex-col items-center gap-1">
          <span className="text-[10px] text-gray-500 font-medium">Bor</span>
          <RecipientBadge
            status="none"
            type="borrower"
            onClick={onBadgeClick}
          />
        </div>
        <div className="flex flex-col items-center gap-1">
          <span className="text-[10px] text-gray-500 font-medium">Co-Bor</span>
          <RecipientBadge
            status="none"
            type="co_borrower"
            onClick={onBadgeClick}
          />
        </div>
        <div className="flex flex-col items-center gap-1">
          <span className="text-[10px] text-gray-500 font-medium">Par</span>
          <RecipientBadge
            status="none"
            type="partner"
            onClick={onBadgeClick}
          />
        </div>
      </div>
    );
  }

  const { borrower, co_borrower, partner } = recipientsSummary;

  // Aggregate multiple partners into a single badge (pipeline row is space-constrained)
  const partnerAggregate = partner && partner.length > 0
    ? (() => {
        const first = partner[0];
        const anyViewed = partner.some(p => p.status === 'viewed');
        const totalViews = partner.reduce((sum, p) => sum + (p.viewCount || 0), 0);
        const lastViewedAt = partner.reduce<string | null | undefined>((latest, p) => {
          if (!p.lastViewedAt) {
            return latest;
          }
          if (!latest) {
            return p.lastViewedAt;
          }
          return new Date(p.lastViewedAt) > new Date(latest) ? p.lastViewedAt : latest;
        }, null);
        const suffix = partner.length > 1 ? ` +${partner.length - 1}` : '';
        return {
          status: (anyViewed ? 'viewed' : 'sent') as 'sent' | 'viewed',
          name: `${first.name}${suffix}`,
          email: destinationFor(first) || '',
          viewCount: totalViews,
          lastViewedAt
        };
      })()
    : null;

  return (
    <div className="flex items-center gap-2">
      {/* Borrower Badge */}
      <div className="flex flex-col items-center gap-1">
        <span className="text-[10px] text-gray-500 font-medium">Bor</span>
        <RecipientBadge
          status={borrower ? borrower.status : 'none'}
          type="borrower"
          recipientName={borrower?.name}
          recipientEmail={destinationFor(borrower)}
          viewCount={borrower?.viewCount}
          lastViewedAt={borrower?.lastViewedAt}
          onClick={onBadgeClick}
        />
      </div>

      {/* Co-Borrower Badge */}
      <div className="flex flex-col items-center gap-1">
        <span className="text-[10px] text-gray-500 font-medium">Co-Bor</span>
        <RecipientBadge
          status={co_borrower ? co_borrower.status : 'none'}
          type="co_borrower"
          recipientName={co_borrower?.name}
          recipientEmail={destinationFor(co_borrower)}
          viewCount={co_borrower?.viewCount}
          lastViewedAt={co_borrower?.lastViewedAt}
          onClick={onBadgeClick}
        />
      </div>

      {/* Partner Badge (aggregated across multiple partners) */}
      <div className="flex flex-col items-center gap-1">
        <span className="text-[10px] text-gray-500 font-medium">Par</span>
        <RecipientBadge
          status={partnerAggregate ? partnerAggregate.status : 'none'}
          type="partner"
          recipientName={partnerAggregate?.name}
          recipientEmail={partnerAggregate?.email}
          viewCount={partnerAggregate?.viewCount}
          lastViewedAt={partnerAggregate?.lastViewedAt}
          onClick={onBadgeClick}
        />
      </div>
    </div>
  );
};
